import {
  type QueryClient,
  type QueryKey,
  type UseQueryOptions,
  hashKey,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useEffect, useSyncExternalStore } from 'react'

import { accessRefused } from './client'

/**
 * The shared reader: reads this browser was refused, put away until a read
 * of each is allowed again.
 *
 * React Query keeps the last good answer beside a failed refetch, which is
 * right for an outage and wrong for a refusal. A 401 or 403 is the instance
 * looking at who asked and declining, so what it answered before is no longer
 * this browser's to show. A refusal therefore seals the read's key, and the
 * seal holds through a 503, a dropped connection, a return to the screen and a
 * write the browser made to its own cache. None of them is the instance
 * agreeing to answer this reader now.
 *
 * Where the seal lives, and what moves it:
 *
 * - **Beside the query client.** The screen is the thing that goes, so a seal
 *   kept in a component is forgotten by the next one to mount, which would
 *   find the refused answer still in the cache and draw it. The seal is kept
 *   for the client, keyed by the query's own hash, so what is sealed is that
 *   answer and nothing else.
 * - **Inside the read.** A refused read seals its key, and a read the
 *   instance answered lifts the seal. Nothing else does: not a rendered error
 *   and not a cache event. A query can look successful for other reasons, and
 *   none of them is evidence about what this reader may see.
 * - **Ordered by generation.** Every refusal in a scope moves that scope's
 *   generation. A read takes the generation before it goes and lifts the seal
 *   only if it is unchanged when the answer comes back, so a read that left
 *   before a refusal and lands after it cannot undo it.
 * - **Never lifted by a manual write.** `setQueryData` does not run the read,
 *   so it cannot reach the lift.
 * - **Dropped when the last reader goes.** A refused answer left in the cache
 *   is a refused answer one bug away from being drawn, so when its key has no
 *   reader left it is removed.
 * - **Seen by every reader at once.** A seal is a rendering input, not a
 *   value read in passing: each reader subscribes to the seals, and a seal
 *   put on from outside the read is announced. A reader showing a key that another reader,
 *   a write or a departed screen's callback has just sealed draws again at
 *   once and withholds it, without waiting for its own query to change.
 * - **Keyed by the session era.** The era is the last part of every key, so
 *   the next session reads keys nothing has been written under, and an answer
 *   still on its way to the last one settles under a key nobody is reading.
 */
type Seals = {
  /** Sealed reads, by query hash. */
  readonly sealed: Set<string>
  /** Refusals met in each scope, by `era/scope`. */
  readonly generations: Map<string, number>
  /** Readers to tell when a seal is put on. */
  readonly listeners: Set<() => void>
}

function announce(seals: Seals) {
  for (const listener of seals.listeners) listener()
}

const byClient = new WeakMap<QueryClient, Seals>()

function sealsOf(client: QueryClient): Seals {
  let seals = byClient.get(client)
  if (!seals) {
    const made: Seals = { sealed: new Set(), generations: new Map(), listeners: new Set() }
    client.getQueryCache().subscribe((event) => {
      if (event.type !== 'observerRemoved') return
      const { query } = event
      if (query.getObserversCount() === 0 && made.sealed.has(query.queryHash))
        client.getQueryCache().remove(query)
    })
    byClient.set(client, made)
    seals = made
  }
  return seals
}

/** Ask the instance for one answer, keeping the seal on its key. */
async function ask<T>(
  seals: Seals,
  id: string,
  scope: string,
  read: () => Promise<T>,
): Promise<T> {
  const since = seals.generations.get(scope) ?? 0
  try {
    const answer = await read()
    // Not announced: the answer is not in the cache yet, so a reader drawn
    // now would show what was refused. The query's own success draws every
    // reader of this key a moment later, with the answer and the lift together.
    if ((seals.generations.get(scope) ?? 0) === since) seals.sealed.delete(id)
    return answer
  } catch (error) {
    if (accessRefused(error)) {
      seals.sealed.add(id)
      seals.generations.set(scope, (seals.generations.get(scope) ?? 0) + 1)
      // Not announced either: the refusal is this key's own, and its error
      // draws every reader of the key with the seal already on.
    }
    throw error
  }
}

export type SealedQueryOptions<T> = Omit<
  UseQueryOptions<T, Error, T, QueryKey>,
  'queryKey' | 'queryFn'
> & {
  /** What is read, without the session era: the era is added last. */
  key: readonly unknown[]
  /** The session the read is made for. */
  era: number
  /**
   * The reads one refusal speaks for. A refusal of any read in the scope
   * moves its generation, so no read of the scope that was out at the time
   * lifts a seal when it lands.
   */
  scope: string
  read: () => Promise<T>
}

/**
 * A read whose answer is shown only for as long as the instance allows it.
 *
 * `data` is withheld while the key is sealed; `sealed` says so, for the screen
 * to show nothing rather than something old. A failure with no refusal before
 * it leaves the answer standing, for the screen to say it may be old.
 *
 * A sealed read with no failure standing against it -- the cache was written
 * to, say -- has nothing to show and nothing to retry, so it is asked again.
 */
export function useSealedQuery<T>({ key, era, scope, read, ...options }: SealedQueryOptions<T>) {
  const seals = sealsOf(useQueryClient())
  const queryKey = [...key, era]
  const id = hashKey(queryKey)
  const query = useQuery({
    ...options,
    queryKey,
    queryFn: () => ask(seals, id, `${era}/${scope}`, read),
  })
  // Read before anything else, so each is tracked: a property the query
  // result was never asked for does not render again when it changes, and the
  // seal is moved by the read, outside React, with nothing to render it.
  const { data, error, isFetching, refetch } = query
  // Subscribed, so a seal put on this key by anything else draws it again.
  const subscribe = useCallback(
    (listener: () => void) => {
      seals.listeners.add(listener)
      return () => {
        seals.listeners.delete(listener)
      }
    },
    [seals],
  )
  const sealed = useSyncExternalStore(subscribe, () => seals.sealed.has(id))
  const unanswered = sealed && error === null && !isFetching
  useEffect(() => {
    if (unanswered) void refetch()
  }, [unanswered, refetch])
  return { query, queryKey, data: sealed ? undefined : data, sealed }
}

/**
 * Seal a read because the instance refused something else this reader asked.
 *
 * A refused write is the same evidence as a refused read: the instance looked
 * at who asked and declined, so what it answered this reader before is no
 * longer theirs to show. This puts the seal on that read's key and moves its
 * scope's generation exactly as a refused read does, so the answer is
 * withheld at once, a read that set out before the refusal cannot lift it
 * when it lands, and only a read the instance answers afterwards does.
 *
 * `key`, `era` and `scope` are the ones the read was made with.
 */
export function sealAfterRefusal(
  client: QueryClient,
  { key, era, scope }: { key: readonly unknown[]; era: number; scope: string },
): void {
  const seals = sealsOf(client)
  const at = `${era}/${scope}`
  seals.sealed.add(hashKey([...key, era]))
  seals.generations.set(at, (seals.generations.get(at) ?? 0) + 1)
  announce(seals)
}
